class StoreSourcingRequestError extends Error {
  constructor(code, message) {
    super(message || 'We could not submit your sourcing request. Please try again.');
    this.name = 'StoreSourcingRequestError';
    this.code = Number(code) || 500;
  }
}

class StoreSourcingRequest extends HTMLElement {
  constructor() {
    super();
    this.file = null;
    this.submissionId = null;
    this.previewUrl = null;
    this.isSubmitting = false;
    this.abortController = null;
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '/apps/bestdealer/shopify-store-sourcing-requests';
    this.maxFileSize = Math.max(1, Number(this.dataset.maxFileSize) || 20 * 1024 * 1024);

    this.form = this.querySelector('[data-sourcing-form]');
    this.fileInput = this.querySelector('[data-sourcing-file-input]');
    this.dropzone = this.querySelector('[data-sourcing-dropzone]');
    this.dropzoneContent = this.querySelector('[data-sourcing-dropzone-content]');
    this.selectedFile = this.querySelector('[data-sourcing-selected-file]');
    this.imagePreview = this.querySelector('[data-sourcing-image-preview]');
    this.fileMark = this.querySelector('[data-sourcing-file-mark]');
    this.fileName = this.querySelector('[data-sourcing-file-name]');
    this.fileMeta = this.querySelector('[data-sourcing-file-meta]');
    this.fileError = this.querySelector('[data-sourcing-file-error]');
    this.emailInput = this.querySelector('[data-sourcing-email]');
    this.submitButton = this.querySelector('[data-sourcing-submit]');
    this.submitLabel = this.querySelector('[data-sourcing-submit-label]');
    this.spinner = this.querySelector('[data-sourcing-spinner]');
    this.resetButton = this.querySelector('[data-sourcing-reset]');
    this.submitError = this.querySelector('[data-sourcing-submit-error]');
    this.success = this.querySelector('[data-sourcing-success]');
    this.successMessage = this.querySelector('[data-sourcing-success-message]');
    this.newRequestButton = this.querySelector('[data-sourcing-new-request]');
    this.liveRegion = this.querySelector('[data-sourcing-live-region]');

    this.bindEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.revokeImagePreview();
  }

  bindEvents() {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });

    this.fileInput.addEventListener('change', () => {
      this.selectFiles(Array.from(this.fileInput.files || []));
    });

    ['dragenter', 'dragover'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.add('is-dragging');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.remove('is-dragging');
      });
    });

    this.dropzone.addEventListener('drop', (event) => {
      this.selectFiles(Array.from(event.dataTransfer?.files || []));
    });

    this.emailInput?.addEventListener('input', () => {
      this.clearSubmissionId();
      this.updateSubmitState();
    });
    this.resetButton.addEventListener('click', () => this.resetFile());
    this.newRequestButton.addEventListener('click', () => this.resetRequest());
  }

  selectFiles(files) {
    this.clearSubmissionId();
    this.clearErrors();

    if (files.length !== 1) {
      this.showFileError('Please choose one image, Word document, or Excel workbook.');
      this.clearSelectedFile();
      this.updateSubmitState();
      return;
    }

    const file = files[0];
    const validationError = this.validateFile(file);
    if (validationError) {
      this.showFileError(validationError);
      this.clearSelectedFile();
      this.updateSubmitState();
      return;
    }

    this.file = file;
    this.updateFilePreview(file);
    this.fileName.textContent = file.name || 'Selected file';
    this.fileMeta.textContent = `${this.fileTypeLabel(file)} · ${this.formatFileSize(file.size)}`;
    this.fileMark.textContent = this.fileExtension(file).toUpperCase();
    this.dropzoneContent.hidden = true;
    this.selectedFile.hidden = false;
    this.resetButton.hidden = false;
    this.updateSubmitState();
    this.announce(`${this.fileName.textContent} selected. Ready to submit.`);
  }

  validateFile(file) {
    if (!file || file.size === 0) return 'Please choose a valid file and try again.';
    if (file.size > this.maxFileSize) {
      return `This file is larger than ${this.formatFileSize(this.maxFileSize)}. Please choose a smaller file.`;
    }

    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'xls', 'xlsx'];
    if (!allowedExtensions.includes(this.fileExtension(file))) {
      return 'Please choose a JPG, PNG, WebP, Word, or Excel file.';
    }

    return null;
  }

  async submit() {
    if (this.isSubmitting || !this.file) return;

    this.clearErrors();
    const email = String(this.emailInput?.value || '').trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.emailInput.reportValidity();
      return;
    }

    this.abortController?.abort();
    const requestController = new AbortController();
    this.abortController = requestController;
    this.setSubmitting(true);

    try {
      const requestUrl = new URL(this.endpoint, window.location.origin);
      if (requestUrl.origin !== window.location.origin) {
        throw new StoreSourcingRequestError(500, 'Sourcing requests must use the store App Proxy.');
      }

      const formData = new FormData();
      formData.append('email', email);
      formData.append('file', this.file, this.file.name);
      formData.append('submission_id', this.getOrCreateSubmissionId());

      const response = await fetch(requestUrl.toString(), {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: requestController.signal,
      });

      const payload = await this.parseJsonResponse(response);
      const code = Number(payload?.code);
      if (!response.ok || code !== 0) {
        throw new StoreSourcingRequestError(code || response.status, payload?.message);
      }

      this.clearSubmissionId();
      this.renderSuccess(email);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError(error);
    } finally {
      if (this.abortController === requestController) {
        this.abortController = null;
        this.setSubmitting(false);
      }
    }
  }

  async parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (_error) {
      throw new StoreSourcingRequestError(response.status || 500, 'The sourcing service returned an invalid response.');
    }
  }

  renderSuccess(email) {
    this.form.hidden = true;
    this.success.hidden = false;
    this.successMessage.textContent = `Our team will review your file and send the sourcing results to ${email}.`;
    this.success.querySelector('h3')?.focus?.({ preventScroll: true });
    this.success.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    this.announce('Your sourcing request has been received.');
  }

  renderError(error) {
    const message = error instanceof StoreSourcingRequestError
      ? error.message
      : 'We could not submit your sourcing request. Please try again.';
    this.submitError.textContent = message;
    this.submitError.hidden = false;
    this.announce(message);
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    this.submitButton.disabled = isSubmitting || !this.canSubmit();
    this.submitButton.setAttribute('aria-busy', String(isSubmitting));
    this.submitLabel.textContent = isSubmitting ? 'Submitting request' : 'Submit sourcing request';
    this.spinner.hidden = !isSubmitting;
    this.resetButton.disabled = isSubmitting;
    this.fileInput.disabled = isSubmitting;
    if (this.emailInput) this.emailInput.disabled = isSubmitting;
  }

  updateSubmitState() {
    this.submitButton.disabled = this.isSubmitting || !this.canSubmit();
  }

  canSubmit() {
    return Boolean(this.file && this.emailInput?.value?.trim());
  }

  resetFile() {
    this.clearSubmissionId();
    this.clearSelectedFile();
    this.clearErrors();
    this.updateSubmitState();
    this.fileInput.focus();
  }

  clearSelectedFile() {
    this.file = null;
    this.revokeImagePreview();
    this.fileInput.value = '';
    this.dropzoneContent.hidden = false;
    this.selectedFile.hidden = true;
    this.resetButton.hidden = true;
  }

  updateFilePreview(file) {
    this.revokeImagePreview();

    if (!['jpg', 'jpeg', 'png', 'webp'].includes(this.fileExtension(file))) {
      return;
    }

    this.previewUrl = URL.createObjectURL(file);
    this.imagePreview.src = this.previewUrl;
    this.imagePreview.alt = `Preview of ${file.name || 'selected image'}`;
    this.imagePreview.hidden = false;
    this.fileMark.hidden = true;
  }

  revokeImagePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;

    if (!this.imagePreview || !this.fileMark) return;
    this.imagePreview.removeAttribute('src');
    this.imagePreview.alt = '';
    this.imagePreview.hidden = true;
    this.fileMark.hidden = false;
  }

  resetRequest() {
    this.form.reset();
    this.resetFile();
    this.form.hidden = false;
    this.success.hidden = true;
    this.querySelector('[data-search-panel-heading]')?.focus({ preventScroll: true });
  }

  getOrCreateSubmissionId() {
    if (!this.submissionId) this.submissionId = window.crypto.randomUUID();
    return this.submissionId;
  }

  clearSubmissionId() {
    this.submissionId = null;
  }

  clearErrors() {
    this.fileError.hidden = true;
    this.fileError.textContent = '';
    this.submitError.hidden = true;
    this.submitError.textContent = '';
  }

  showFileError(message) {
    this.fileError.textContent = message;
    this.fileError.hidden = false;
    this.announce(message);
  }

  fileExtension(file) {
    return String(file?.name || '').split('.').pop().toLowerCase();
  }

  fileTypeLabel(file) {
    const extension = this.fileExtension(file);
    if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) return 'Image';
    if (['doc', 'docx'].includes(extension)) return 'Word document';
    return 'Excel workbook';
  }

  formatFileSize(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  announce(message) {
    this.liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('store-sourcing-request')) {
  customElements.define('store-sourcing-request', StoreSourcingRequest);
}
